import React, { useMemo } from 'react';
import { Document, Page, View, Text, Image, StyleSheet, PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { Box, Button } from '@mui/material';
import { pdfTheme } from '../../../utils/pdfTheme';
import { buildPivotData, studentName } from './hubUtils';

const c = pdfTheme.colors;

const s = StyleSheet.create({
  page:         { padding: '8mm 7mm', fontSize: 6.5, fontFamily: 'Helvetica', backgroundColor: '#fff' },

  // ── Header ──────────────────────────────────────────────────────────────────
  header:       { flexDirection: 'row', alignItems: 'center', marginBottom: 5, paddingBottom: 5, borderBottom: `2pt solid ${c.primary}`, gap: 8 },
  logoBox:      { width: 55, height: 55, alignItems: 'center', justifyContent: 'center', borderRadius: 2 },
  headerCenter: { flex: 1, alignItems: 'center' },
  schoolName:   { fontSize: 12, fontFamily: 'Helvetica-Bold', color: c.primary, textTransform: 'uppercase', textAlign: 'center', letterSpacing: 0.5 },
  motto:        { fontSize: 6.5, color: '#555', fontStyle: 'italic', textAlign: 'center', marginTop: 1 },
  address:      { fontSize: 6, color: '#666', textAlign: 'center', marginTop: 1 },
  docTitle:     { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#fff', backgroundColor: c.primary, textAlign: 'center', marginTop: 4, textTransform: 'uppercase', paddingVertical: 2, paddingHorizontal: 10, borderRadius: 2 },

  // ── Meta strip ──────────────────────────────────────────────────────────────
  metaStrip:    { flexDirection: 'row', backgroundColor: '#f5f5f5', borderRadius: 2, padding: '4 6', marginBottom: 5, gap: 14 },
  metaItem:     { fontSize: 6.5, color: '#555' },
  metaBold:     { fontFamily: 'Helvetica-Bold', color: '#222' },

  // ── Table ───────────────────────────────────────────────────────────────────
  table:        { width: '100%', borderLeft: `0.5pt solid ${c.border}`, borderTop: `0.5pt solid ${c.border}` },
  theadTop:     { flexDirection: 'row', backgroundColor: c.primary },
  theadSub:     { flexDirection: 'row', backgroundColor: '#388e3c' },
  th:           { fontFamily: 'Helvetica-Bold', color: '#fff', padding: '8 5', borderRight: `0.5pt solid rgba(255,255,255,0.25)`, textAlign: 'center', fontSize: 8 },
  thLeft:       { textAlign: 'left' },
  row:          { flexDirection: 'row', borderBottom: `0.5pt solid ${c.border}` },
  rowAlt:       { backgroundColor: '#f9fbe7' },
  td:           { fontSize: 9, padding: '7 5', borderRight: `0.5pt solid ${c.border}`, textAlign: 'center' },
  tdLeft:       { textAlign: 'left' },
  tdBold:       { fontFamily: 'Helvetica-Bold' },
  tdGray:       { color: '#bbb', fontStyle: 'italic' },
  tdGreen:      { color: c.primary, fontFamily: 'Helvetica-Bold' },
  tdBlue:       { color: '#1565c0', fontFamily: 'Helvetica-Bold' },

  // ── Summary boxes ───────────────────────────────────────────────────────────
  summaryWrap:  { marginTop: 5, paddingTop: 5, borderTop: `2pt solid ${c.primary}` },
  summaryRow:   { flexDirection: 'row', gap: 8 },
  summaryBox:   { flex: 1, padding: '4 6', backgroundColor: '#f5f5f5', borderLeft: `3pt solid ${c.primary}` },
  summaryLabel: { fontFamily: 'Helvetica-Bold', color: '#777', fontSize: 6, marginBottom: 2 },
  summaryValue: { fontFamily: 'Helvetica-Bold', color: c.primary, fontSize: 10 },

  // ── Page footer ─────────────────────────────────────────────────────────────
  pageFooter:   { borderTop: `1pt solid ${c.border}`, marginTop: 5, paddingTop: 3, fontSize: 5.5, color: '#aaa', textAlign: 'center' },
});

const divColor = (div) => {
  if (!div) return '#888';
  if (div.includes('1')) return '#1565c0';
  if (div.includes('2')) return '#2e7d32';
  if (div.includes('3')) return '#e65100';
  if (div.includes('4')) return '#c62828';
  return '#888';
};

const markStr = (a) => {
  if (!a) return '-';
  if (a.is_absent) return 'ABS';
  if (a.is_exempted) return 'EXM';
  const v = parseFloat(a.score ?? a.marks_obtained ?? a.raw_score ?? 0);
  return isNaN(v) ? '-' : v.toFixed(0);
};

// ── Shared page renderer ─────────────────────────────────────────────────────
const StudentPage = ({ student, row, subjects, assessmentCols, isSingleType, classLabel, termLabel, schoolName, schoolMotto, schoolAddress, schoolPhone, schoolLogo, pageKey }) => {
  const cols = assessmentCols.length > 0 ? assessmentCols : [{ code: 'Marks', max_marks: '-' }];

  // Width layout:
  // isSingleType:  Subject | Mark | Grade  (one mark col, one grade col)
  // multi/none:    Subject | Mark+Grade per col  (paired cols)
  const subjectW  = 38;
  const gradeW    = 9;   // per-col grade width
  const remaining = 100 - subjectW;
  // each col gets markW + gradeW
  const markW = parseFloat(((remaining / cols.length) - gradeW).toFixed(2));
  // for single type: one mark col + one grade col
  const singleMarkW  = parseFloat((remaining * 0.6).toFixed(2));
  const singleGradeW = parseFloat((remaining * 0.4).toFixed(2));

  const pct = (w) => `${w}%`;

  const total      = parseFloat(row.total ?? 0);
  const aggregate  = row.aggregate ?? null;
  const division   = row.division  ?? null;

  return (
    <Page key={pageKey} size="A4" style={s.page}>

      {/* ── Header ── */}
      <View style={s.header}>
        {schoolLogo && (
          <View style={s.logoBox}>
            <Image src={schoolLogo} style={{ width: 51, height: 51, objectFit: 'contain' }} />
          </View>
        )}
        <View style={s.headerCenter}>
          <Text style={s.schoolName}>{schoolName}</Text>
          {schoolMotto   ? <Text style={s.motto}>{schoolMotto}</Text>   : null}
          {schoolAddress ? <Text style={s.address}>{schoolAddress}</Text> : null}
          {schoolPhone   ? <Text style={s.address}>Tel: {schoolPhone}</Text> : null}
          <Text style={s.docTitle}>Student Assessment Report</Text>
        </View>
      </View>

      {/* ── Meta strip ── */}
      <View style={s.metaStrip}>
        {[
          ['Student', studentName(student)],
          ['Reg. No',  student.registration_no || '-'],
          ['Class',    classLabel || student.class_stream_name || '-'],
          ['Term',     termLabel],
          ['Gender',   student.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1) : '-'],
        ].map(([label, val]) => (
          <Text key={label} style={s.metaItem}>
            <Text style={s.metaBold}>{label}: </Text>{val}
          </Text>
        ))}
      </View>

      {/* ── Table ── */}
      <View style={s.table}>

        {/* Header row 1 */}
        <View style={s.theadTop} fixed>
          <View style={[s.th, s.thLeft, { width: pct(subjectW) }]}><Text>Subject</Text></View>
          {isSingleType ? (
            <>
              <View style={[s.th, { width: pct(singleMarkW) }]}>
                <Text>{cols[0].code} Marks</Text>
              </View>
              <View style={[s.th, { width: pct(singleGradeW) }]}><Text>Grade</Text></View>
            </>
          ) : (
            cols.map((col) => (
              <View key={col.code} style={[s.th, { width: pct(markW + gradeW), borderLeft: '0.5pt solid rgba(255,255,255,0.4)' }]}>
                <Text>{col.code} Marks</Text>
              </View>
            ))
          )}
        </View>

        {/* Header row 2 — Grade sub-headers (multi only, no max marks needed) */}
        {!isSingleType && (
          <View style={s.theadSub} fixed>
            <View style={[s.th, { width: pct(subjectW) }]}><Text> </Text></View>
            {cols.map((col) => (
              <React.Fragment key={col.code}>
                <View style={[s.th, { width: pct(markW), fontSize: 7.5 }]}><Text>Marks</Text></View>
                <View style={[s.th, { width: pct(gradeW), fontSize: 7.5 }]}><Text>Grade</Text></View>
              </React.Fragment>
            ))}
          </View>
        )}

        {/* Data rows — one per subject */}
        {subjects.map((subj, idx) => {
          const byCode = row.marksBySubject[subj.id] || {};

          return (
            <View key={subj.id} wrap={false} style={[s.row, idx % 2 !== 0 ? s.rowAlt : {}]}>
              <View style={[s.td, s.tdLeft, s.tdBold, { width: pct(subjectW) }]}>
                <Text>{subj.name}</Text>
              </View>
              {isSingleType ? (() => {
                const a = byCode[cols[0]?.code];
                const val = markStr(a);
                const isSpecial = val === 'ABS' || val === 'EXM';
                const grade = (!a || a.is_absent || a.is_exempted) ? '-' : (a.assessment_grade || '-');
                return (
                  <>
                    <View style={[s.td, { width: pct(singleMarkW) }]}>
                      <Text style={isSpecial ? s.tdGray : s.tdBold}>{val}</Text>
                    </View>
                    <View style={[s.td, { width: pct(singleGradeW), fontFamily: 'Helvetica-Bold', color: grade === 'F' ? '#d32f2f' : grade !== '-' ? '#1565c0' : '#aaa' }]}>
                      <Text>{grade}</Text>
                    </View>
                  </>
                );
              })() : (
                cols.map((col) => {
                  const a = byCode[col.code];
                  const val = markStr(a);
                  const isSpecial = val === 'ABS' || val === 'EXM';
                  const grade = (!a || a.is_absent || a.is_exempted) ? '-' : (a.assessment_grade || '-');
                  return (
                    <React.Fragment key={col.code}>
                      <View style={[s.td, { width: pct(markW) }]}>
                        <Text style={isSpecial ? s.tdGray : s.tdBold}>{val}</Text>
                      </View>
                      <View style={[s.td, { width: pct(gradeW), fontFamily: 'Helvetica-Bold', color: grade === 'F' ? '#d32f2f' : grade !== '-' ? '#1565c0' : '#aaa' }]}>
                        <Text>{grade}</Text>
                      </View>
                    </React.Fragment>
                  );
                })
              )}
            </View>
          );
        })}
      </View>

      {/* ── Summary boxes ── */}
      <View style={s.summaryWrap}>
        <View style={s.summaryRow}>
          <View style={s.summaryBox}>
            <Text style={s.summaryLabel}>Total Marks</Text>
            <Text style={s.summaryValue}>{total.toFixed(0)}</Text>
          </View>
          {isSingleType && (
            <View style={s.summaryBox}>
              <Text style={s.summaryLabel}>Aggregate</Text>
              <Text style={s.summaryValue}>{aggregate ?? '-'}</Text>
            </View>
          )}
          {isSingleType && (
            <View style={[s.summaryBox, { borderLeft: `3pt solid ${divColor(division)}` }]}>
              <Text style={s.summaryLabel}>Division</Text>
              <Text style={[s.summaryValue, { color: divColor(division) }]}>{division ?? '-'}</Text>
            </View>
          )}
        </View>
      </View>

      {/* ── Page footer ── */}
      <View style={s.pageFooter}>
        <Text>
          Raw marks{isSingleType ? ' · Aggregate = best 4 subject grades' : ''} · Generated {new Date().toLocaleString()} · School Sync
        </Text>
      </View>

    </Page>
  );
};

// ── Single-student doc ────────────────────────────────────────────────────────
const StudentAllAssessmentsPdfDoc = ({
  student,
  studentMarks,
  assessmentConfigs,
  filters,
  classes,
  terms,
  school,
  gradingBoundaries = [],
}) => {
  const schoolName    = school?.name    || 'School Name';
  const schoolMotto   = school?.motto   || '';
  const schoolAddress = [school?.address, school?.city, school?.district].filter(Boolean).join(', ') || '';
  const schoolPhone   = school?.phone   || '';
  const schoolLogo    = school?.logo    || null;

  const classRecord = useMemo(
    () => classes.find((cl) => String(cl.id) === String(filters.class_id)),
    [classes, filters.class_id]
  );
  const termRecord = useMemo(
    () => terms.find((t) => String(t.id) === String(filters.term_id)),
    [terms, filters.term_id]
  );

  const assessmentTypeIds = useMemo(() => filters?.assessment_type_ids || [], [filters?.assessment_type_ids]);

  const { subjects, rows, assessmentCols, isSingleType } = useMemo(
    () => buildPivotData(studentMarks, assessmentConfigs, null, gradingBoundaries, assessmentTypeIds),
    [studentMarks, assessmentConfigs, gradingBoundaries, assessmentTypeIds]
  );

  const row = rows[0] || { marksBySubject: {}, total: 0, aggregate: null, division: null, overall_percentage: 0 };
  const classLabel = classRecord?.name || student.class_stream_name || '-';
  const termLabel  = `${termRecord?.name || '-'} ${filters.academic_year || ''}`.trim();

  return (
    <Document>
      <StudentPage
        student={student}
        row={row}
        subjects={subjects}
        assessmentCols={assessmentCols}
        isSingleType={isSingleType}
        classLabel={classLabel}
        termLabel={termLabel}
        schoolName={schoolName}
        schoolMotto={schoolMotto}
        schoolAddress={schoolAddress}
        schoolPhone={schoolPhone}
        schoolLogo={schoolLogo}
      />
    </Document>
  );
};

const StudentAllAssessmentsPdf = (props) => (
  <div style={{ width: '100%' }}>
    <Box mb={1} display="flex" gap={1}>
      <PDFDownloadLink
        document={<StudentAllAssessmentsPdfDoc {...props} />}
        fileName={`student-marks-${studentName(props.student).replace(/\s+/g, '_')}-${new Date().toISOString().split('T')[0]}.pdf`}
      >
        {({ loading }) => (
          <Button variant="contained" size="small" disabled={loading}>
            {loading ? 'Generating…' : 'Download PDF'}
          </Button>
        )}
      </PDFDownloadLink>
    </Box>
    <div style={{ width: '100%', height: '560px', border: '1px solid #ddd' }}>
      <PDFViewer style={{ width: '100%', height: '100%' }}>
        <StudentAllAssessmentsPdfDoc {...props} />
      </PDFViewer>
    </div>
  </div>
);

export default StudentAllAssessmentsPdf;
export { StudentAllAssessmentsPdfDoc };

// ── Bulk PDF: one page per student ───────────────────────────────────────────
export const BulkStudentPdfDoc = ({
  reportCards,
  assessmentConfigs,
  filters,
  classes,
  terms,
  school,
  gradingBoundaries = [],
  students = [],
}) => {
  const schoolName    = school?.name    || 'School Name';
  const schoolMotto   = school?.motto   || '';
  const schoolAddress = [school?.address, school?.city, school?.district].filter(Boolean).join(', ') || '';
  const schoolPhone   = school?.phone   || '';
  const schoolLogo    = school?.logo    || null;

  const classRecord = classes.find((cl) => String(cl.id) === String(filters.class_id));
  const termRecord  = terms.find((t)  => String(t.id)  === String(filters.term_id));
  const classLabel  = classRecord?.name || '-';
  const termLabel   = `${termRecord?.name || '-'} ${filters.academic_year || ''}`.trim();

  // Build a quick lookup from the full students list for name resolution
  const studentsById = Object.fromEntries(students.map((s) => [String(s.id), s]));

  const assessmentTypeIds = filters?.assessment_type_ids || [];
  const { subjects, assessmentCols, isSingleType } = buildPivotData(
    reportCards, assessmentConfigs, null, gradingBoundaries, assessmentTypeIds
  );

  return (
    <Document>
      {reportCards.map((rc) => {
        // Resolve student: prefer rc.student, fall back to students list
        const rcStudent = rc.student || studentsById[String(rc.student_id)] || { id: rc.student_id };
        const student = {
          ...rcStudent,
          full_name: rcStudent.full_name ||
            `${rcStudent.last_name || ''} ${rcStudent.first_name || ''}`.trim() ||
            null,
        };
        const { rows } = buildPivotData([rc], assessmentConfigs, null, gradingBoundaries, assessmentTypeIds);
        const row = rows[0] || { marksBySubject: {}, total: 0, aggregate: null, division: null, overall_percentage: 0 };

        return (
          <StudentPage
            key={rc.student_id}
            pageKey={rc.student_id}
            student={student}
            row={row}
            subjects={subjects}
            assessmentCols={assessmentCols}
            isSingleType={isSingleType}
            classLabel={classLabel}
            termLabel={termLabel}
            schoolName={schoolName}
            schoolMotto={schoolMotto}
            schoolAddress={schoolAddress}
            schoolPhone={schoolPhone}
            schoolLogo={schoolLogo}
          />
        );
      })}
    </Document>
  );
};
